import {
  Button,
  EmptyContainer,
  ICONS,
  PLATFORM_THEME_COLORS,
  SearchInput,
  Typography,
  TYPOGRAPHY_SIZE,
  EMPTY_ILLUSTRATIONS,
  DeleteModal,
} from '@thinknet-gmbh/platform-common-ui';
import {
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
  ProjectStatusesEnum,
} from '@thinknet-gmbh/platform-common';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMembers } from 'src/hooks/members/get-members.hook';
import { useDeleteInternalMember } from 'src/hooks/members/delete-internal-member.hook';
import PermissionCheck from 'src/components/permission-check';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

import { ChangeUserPermission } from './components/change-user-permission';
import { CreateUserModal } from './components/create-user-modal';
import { UserManagementTable } from './components/user-management-table';
import { USER_MANAGEMENT_MODALS } from './user-management.constants';
import { HeaderContainer, SearchBox, UserManagementWrapper } from './user-management.styles';
import { useGetProjectsByProjectOwner } from 'src/hooks/projects/get-projects-by-projects-owner.hook';
import { TransferProjectsModal } from './components/transfer-projects-modal';
import { EditUserPosition } from './components/edit-user-position';
import EditUserModal from './components/create-user-modal/edit-user-modal.component';

const UserManagement = () => {
  const { t } = useTranslation(['common', 'settings']);
  const currentCompanyId = useGetCurrentCompanyId();
  const [selectedUser, setSelectedUser] = useState<any>();
  const [activeModal, setActiveModal] = useState<null | string>(null);
  const [filter, setFilter] = useState({ companyId: currentCompanyId, searchParam: '' });

  const deleteInternalMember = useDeleteInternalMember();
  const { projects, totalCount } = useGetProjectsByProjectOwner(selectedUser && selectedUser._id, [
    ProjectStatusesEnum.ACTIVE,
  ]);

  const { members, paginationConfig, loading } = useGetMembers(filter);

  useEffect(() => {
    if (currentCompanyId) {
      setFilter(prev => ({ ...prev, companyId: currentCompanyId }));
    }
  }, [currentCompanyId]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setFilter(prevValue => ({ ...prevValue, searchParam: e.target.value }));
  };

  const deleteUser = () => {
    deleteInternalMember({ variables: { memberId: selectedUser._id } }).then(() =>
      setActiveModal(null)
    );
  };

  const onDeleteSubmit = () => {
    if (totalCount) {
      setActiveModal(USER_MANAGEMENT_MODALS.transferActiveProjects);
    } else {
      deleteUser();
    }
  };

  const closeModal = () => setActiveModal(null);

  return (
    <UserManagementWrapper>
      {activeModal === USER_MANAGEMENT_MODALS.editUserPosition && (
        <EditUserPosition onClose={closeModal} selectedUser={selectedUser} />
      )}
      {activeModal === USER_MANAGEMENT_MODALS.createUser && (
        <CreateUserModal onClose={closeModal} />
      )}
      {activeModal === USER_MANAGEMENT_MODALS.editUser && (
        <EditUserModal onClose={closeModal} selectedUser={selectedUser} />
      )}
      {activeModal === USER_MANAGEMENT_MODALS.deleteUser && (
        <DeleteModal
          isOpened
          title={t('settings:userManagementSettings.deleteModal.title')}
          subTitle={t('settings:userManagementSettings.deleteModal.subtitle', {
            firstName: selectedUser.profile.firstName,
            lastName: selectedUser.profile.lastName,
          })}
          onClose={closeModal}
          onSubmit={onDeleteSubmit}
          translate={t}
        />
      )}
      {activeModal === USER_MANAGEMENT_MODALS.changePermissionGroup && (
        <ChangeUserPermission
          onClose={closeModal}
          selectedUser={selectedUser}
          companyId={currentCompanyId}
        />
      )}
      {activeModal === USER_MANAGEMENT_MODALS.transferActiveProjects && (
        <TransferProjectsModal
          projects={projects}
          selectedEmployee={selectedUser}
          onClose={closeModal}
          onFinish={() => deleteUser()}
        />
      )}
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('common:labels.userManagement')}
        </Typography>
        <SearchBox>
          <SearchInput value={filter.searchParam} onChange={handleSearch} />
          <PermissionCheck
            action={PERMISSION_ACTIONS.create}
            permission={PLATFORM_PERMISSIONS.memberManagement}
          >
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={() => setActiveModal(USER_MANAGEMENT_MODALS.createUser)}
            >
              {t('settings:userManagementSettings.labels.addANewUser')}
            </Button>
          </PermissionCheck>
        </SearchBox>
      </HeaderContainer>
      {members.length > 0 ? (
        <UserManagementTable
          loading={loading}
          dataSource={members}
          handleModals={(record, modal) => {
            setSelectedUser(record);
            setActiveModal(modal);
          }}
          pagination={paginationConfig}
        />
      ) : (
        <EmptyContainer
          illustration={EMPTY_ILLUSTRATIONS.seo}
          emptyTitle={t('common:emptySearch.title')}
          contentText={t('common:emptySearch.subTitle')}
        />
      )}
    </UserManagementWrapper>
  );
};

export default UserManagement;
